import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

import { FileAttachment } from '../../types/upload';
import type {
  FeedbackRating,
  WorkflowStep,
  MessageFeedbackState,
} from '../../types/feedback';
import AttachmentButton from './AttachmentButton';
import AttachmentPreviewBar from './AttachmentPreviewBar';
import MessageFeedback from '../chat/MessageFeedback';

/**
 * Attachment info stored in messages for display.
 */
export interface MessageAttachment {
  id: string;
  name: string;
  type: string;
  category: 'image' | 'document';
  preview?: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  /** Attachments included with this message */
  attachments?: MessageAttachment[];
}

/** Maximum allowed message length (characters) */
const MAX_MESSAGE_LENGTH = 10000;

interface ChatInterfaceProps {
  messages: Message[];
  /** Callback when sending a message (with optional attachments) */
  onSendMessage: (content: string, attachments?: FileAttachment[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  height?: string | number;
  /** Enable file attachments (default: false for backward compatibility) */
  enableAttachments?: boolean;
  /** Current attachments being staged (controlled externally) */
  attachments?: FileAttachment[];
  /** Callback to add files */
  onFilesSelected?: (files: File[]) => void;
  /** Callback to remove an attachment */
  onRemoveAttachment?: (id: string) => void;
  /** Whether files are uploading */
  isUploading?: boolean;
  /** Upload progress (0-100) */
  uploadProgress?: number;
  /** Maximum message length (default: 10000) */
  maxLength?: number;
  /** Enable feedback buttons on assistant messages */
  enableFeedback?: boolean;
  /** Project ID for feedback (required if enableFeedback is true) */
  projectId?: string;
  /** Workflow step for feedback (required if enableFeedback is true) */
  step?: WorkflowStep;
  /** Track which messages have received feedback */
  messageFeedback?: MessageFeedbackState;
  /** Callback when feedback is submitted */
  onFeedbackSubmit?: (messageIndex: number, rating: FeedbackRating) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  isLoading = false,
  placeholder = 'Type your message...',
  height = '600px',
  enableAttachments = false,
  attachments = [],
  onFilesSelected,
  onRemoveAttachment,
  isUploading = false,
  uploadProgress = 0,
  maxLength = MAX_MESSAGE_LENGTH,
  enableFeedback = false,
  projectId,
  step,
  messageFeedback = {},
  onFeedbackSubmit,
}) => {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;

    // Validate length
    if (value.length > maxLength) {
      setInputError(
        `Message too long (${value.length}/${maxLength} characters)`
      );
    } else {
      setInputError(null);
    }

    // Always update input (allow typing even if over limit, for editing)
    setInput(value);
  };

  const handleSend = () => {
    const trimmedInput = input.trim();
    const hasContent = trimmedInput.length > 0;
    const hasAttachments = attachments.length > 0;

    // Validate before sending
    if (trimmedInput.length > maxLength) {
      setInputError(
        `Message too long (${trimmedInput.length}/${maxLength} characters)`
      );
      return;
    }

    if (hasContent || hasAttachments) {
      onSendMessage(
        trimmedInput,
        attachments.length > 0 ? attachments : undefined
      );
      setInput('');
      setInputError(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFilesSelected = (files: File[]) => {
    if (onFilesSelected) {
      onFilesSelected(files);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    if (onRemoveAttachment) {
      onRemoveAttachment(id);
    }
  };

  // Determine if send is disabled
  const isSendDisabled =
    isLoading ||
    isUploading ||
    inputError !== null ||
    (!input.trim() && attachments.length === 0);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: height,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: theme.palette.background.default,
        }}
      >
        {messages.map((msg, msgIndex) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                maxWidth: '80%',
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor:
                    msg.role === 'user'
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                  width: 32,
                  height: 32,
                }}
              >
                {msg.role === 'user' ? (
                  <PersonIcon fontSize="small" />
                ) : (
                  <SmartToyIcon fontSize="small" />
                )}
              </Avatar>

              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  bgcolor:
                    msg.role === 'user'
                      ? theme.palette.primary.light
                      : theme.palette.background.paper,
                  color:
                    msg.role === 'user'
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                  borderRadius: 2,
                  borderTopRightRadius: msg.role === 'user' ? 0 : 2,
                  borderTopLeftRadius: msg.role === 'assistant' ? 0 : 2,
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>

                {/* Show attachments in user messages */}
                {msg.attachments && msg.attachments.length > 0 && (
                  <Box
                    sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                  >
                    {msg.attachments.map((att) => (
                      <Box
                        key={att.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          borderRadius: 1,
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {att.category === 'image' && att.preview ? (
                          <Box
                            component="img"
                            src={att.preview}
                            alt={att.name}
                            sx={{
                              width: 20,
                              height: 20,
                              objectFit: 'cover',
                              borderRadius: 0.5,
                            }}
                          />
                        ) : null}
                        <Typography
                          variant="caption"
                          sx={{
                            maxWidth: 100,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {att.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Feedback buttons for assistant messages */}
                {enableFeedback &&
                  msg.role === 'assistant' &&
                  projectId &&
                  step && (
                    <MessageFeedback
                      projectId={projectId}
                      step={step}
                      messageIndex={msgIndex}
                      existingFeedback={messageFeedback[msgIndex]}
                      onFeedbackSubmit={(rating) =>
                        onFeedbackSubmit?.(msgIndex, rating)
                      }
                      disabled={isLoading}
                    />
                  )}
              </Paper>
            </Box>
          </Box>
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 32,
                  height: 32,
                }}
              >
                <SmartToyIcon fontSize="small" />
              </Avatar>
              <Paper sx={{ p: 2, borderRadius: 2, borderTopLeftRadius: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Attachment Preview Bar */}
      {enableAttachments && (
        <AttachmentPreviewBar
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          disabled={isLoading || isUploading}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
      )}

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {/* Attachment Button */}
          {enableAttachments && (
            <AttachmentButton
              onFilesSelected={handleFilesSelected}
              disabled={isLoading || isUploading}
              currentCount={attachments.length}
            />
          )}

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={placeholder}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            variant="outlined"
            size="small"
            disabled={isLoading || isUploading}
            error={!!inputError}
            helperText={inputError}
            inputProps={{ maxLength: maxLength + 100 }} // Allow slight overflow for editing
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={isSendDisabled}
            sx={{ alignSelf: 'flex-end' }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default ChatInterface;
